

import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { lifestyleApi } from "../apiService/api";
import { getUrlParams } from "../apiService/timespam";
import SectionHeader from "../Components/SectionHeader";
import { navigateWithFai, triggerShare } from "../utils/Utils";

const ensureShimmerKeyframes = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("shimmer-keyframes")) return;

  const style = document.createElement("style");
  style.id = "shimmer-keyframes";
  style.innerHTML = `
    @keyframes shimmer {
      0% { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
  `;
  document.head.appendChild(style);
};

const shimmerBaseStyle = {
  position: "relative",
  overflow: "hidden",
  backgroundImage:
    "linear-gradient(90deg, var(--shimmer-1) 0%, var(--shimmer-2) 50%, var(--shimmer-1) 100%)",
  backgroundSize: "200% 100%",
  animation: "shimmer 1.3s linear infinite",
};

const SafebarSkeleton = () => {
  useEffect(() => ensureShimmerKeyframes(), []);

  return (
    <div style={{ width: "100%", backgroundColor: "var(--bg-card)", borderRadius: "20px", border: "1px solid var(--border-subtle)" }}>
      <div style={{ padding: "12px 16px 0 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ ...shimmerBaseStyle, width: 32, height: 32, borderRadius: 999 }} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ ...shimmerBaseStyle, height: 18, width: "60%", borderRadius: 8 }} />
            <div style={{ ...shimmerBaseStyle, height: 14, width: "40%", borderRadius: 8 }} />
          </div>
          <div style={{ ...shimmerBaseStyle, width: 20, height: 20, borderRadius: 999 }} />
        </div>
      </div>

      <div style={{ marginTop: 12, padding: "0 16px 20px 16px" }}>
        <div style={{ ...shimmerBaseStyle, height: 230, borderRadius: 16 }} />
      </div>
    </div>
  );
};

const positionElite = (chart) => {
  const points = chart.series[0].points;
  const elitePoint = points.find((p) => p.category === "Elite");
  if (!elitePoint || !elitePoint.dataLabel) return;

  const labelBox = elitePoint.dataLabel.getBBox();
  const eliteSize = 40;

  const eliteX = labelBox.x + labelBox.width / 2 - eliteSize / 2 + 8;
  const eliteY = labelBox.y - eliteSize - 8;

  if (!chart.customElite) chart.customElite = [];
  chart.customElite.forEach((el) => el.destroy());
  chart.customElite = [];

  const border = chart.renderer
    .circle(
      eliteX + eliteSize / 2,
      eliteY + eliteSize / 2,
      eliteSize / 2 + 4 - 4
    )
    .attr({
      fill: "none",
      stroke: "#4B78C1",
      "stroke-width": 4
    })
    .add();

  const img = chart.renderer
    .image(elitePoint.image, eliteX, eliteY, eliteSize, eliteSize)
    .css({ clipPath: "circle(50% at 50% 50%)" })
    .add();

  border.toFront();
  img.toFront();
  chart.customElite.push(border, img);
};


const Safebar = ({ shareConfig } = {}) => {
  const [chartData, setChartData] = useState(null);

  const iconImages = [
    "https://appcdn.goqii.com/storeimg/33889_1617457094.png",
    "https://goqii-app.s3.amazonaws.com/emailer/60758_1605782911.png",
    "https://goqii-app.s3.amazonaws.com/emailer/42309_1605782941.png",
    "https://goqii-app.s3.amazonaws.com/emailer/36689_1605782957.png"
  ];

  useEffect(() => {
    const { encryptedId } = getUrlParams();

    lifestyleApi
      .safebarData(encryptedId)
      .then((response) => {
        if (response?.data?.code === 200) {
          setChartData(response.data.data);
        }
      })
      .catch(console.error);
  }, []);

  if (!chartData) return <SafebarSkeleton />;

  const options = {
    chart: {
      type: "column",
      backgroundColor: "transparent",
      spacingBottom: 40,
      height: 210,

      events: {
        load() { setTimeout(() => positionElite(this), 20); },
        render() {
          setTimeout(() => positionElite(this), 20);

          const series = this.series[0].points;
          const iconSize = 34;
          const axisY = this.chartHeight - 38;

          if (!this.customAxisIcons) this.customAxisIcons = [];
          this.customAxisIcons.forEach(el => el.destroy());
          this.customAxisIcons = [];

          series.forEach((point, index) => {
            const x = this.xAxis[0].toPixels(point.x, true) - iconSize / 2 + 9;

            const icon = this.renderer
              .image(iconImages[index], x, axisY, iconSize, iconSize)
              .css({ clipPath: "circle(50% at 50% 50%)" })
              .add();

            this.customAxisIcons.push(icon);
          });
        },
        redraw() { setTimeout(() => positionElite(this), 20); },
        resize() { this.reflow(); setTimeout(() => positionElite(this), 20); }
      }
    },
    title: {
      text: "",
      enabled: false
    },

    legend: {
      enabled: false
    },
    credits: {
      enabled: false
    },
    tooltip: {
      enabled: false
    },

    xAxis: {
      categories: chartData.series.map((s) => s.label),
      tickLength: 0,
      labels: {
        y: 17,
        style: { fontSize: "13px", fontWeight: "600", color: "var(--text-primary)" }
      }
    },

    yAxis: { visible: false, max: 200 },

    plotOptions: {
      column: {
        dataLabels: {
          enabled: true,
          formatter() { return this.y + "%"; },
          style: { color: "var(--text-primary)" }
        }
      }
    },

    series: [
      {
        data: chartData.series.map((s) => ({
          y: s.y,
          color: s.color,
          image: s.image
        }))
      }
    ]
  };


  return (
    <div style={{ width: "100%", backgroundColor: "var(--bg-card)", borderRadius: "20px", border: "1px solid var(--border-subtle)" }}>
      <SectionHeader
        icon="https://appcdn.goqii.com/storeimg/39075_1764052884.png"
        title={chartData.title}
        subtitle={chartData.heading}
        infoIconLight="https://appcdn.goqii.com/storeimg/38298_1764052060.png"
        infoIconDark="https://appcdn.goqii.com/storeimg/26151_1769593659.png"
        showInfo
        onInfoClick={() => navigateWithFai("My Goqii Health", chartData.infoUrl)}
        showShare={!!shareConfig?.isSharable}
        onShareClick={() => triggerShare(shareConfig?.text)}
      />


      <HighchartsReact highcharts={Highcharts} options={options} />


      <div
        style={{
          textAlign: "left",
          padding: "8px 16px",
          fontSize: "9px",
          color: "var(--text-secondary)"
        }}
      >
        {chartData.subText}
      </div>
    </div>
  );
};

export default Safebar;
